import React, { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "../../ui/dialog";
import { toast } from "sonner";
import {
  RefreshCw, Plus, CheckCircle, Clock, Trash2, AlertTriangle, Bell,
  EyeOff, RotateCcw,
} from "lucide-react";
import DatePickerField from "../DatePickerField";
import { AdminPageHeader, AdminFilterTabs } from "../ui";

const PRIORITIES = [
  { value: "laag", label: "Laag", color: "bg-gray-100 text-gray-700" },
  { value: "normaal", label: "Normaal", color: "bg-blue-100 text-blue-700" },
  { value: "hoog", label: "Hoog", color: "bg-orange-100 text-orange-700" },
  { value: "urgent", label: "Urgent", color: "bg-red-100 text-red-700" },
];

// Statuslabel — "gesloten/geannuleerd/uitgesteld" tonen we onder één
// vlag "Gesloten" zodat de medewerker niet onnodig drie smaakjes ziet.
const STATUS_LABELS = {
  open: "Open",
  afgerond: "Afgerond",
  uitgesteld: "Gesloten",
  geannuleerd: "Gesloten",
  gesloten: "Gesloten",
};
const REPEAT_LABELS = { geen: "Geen", dagelijks: "Dagelijks", wekelijks: "Wekelijks", maandelijks: "Maandelijks" };
const LINK_LABELS = { offerte: "Offerte", klant: "Klant", pickup: "Pickup" };

// Filter-tabs ↔ querystring naar /api/admin/reminders.
//  open:      status=open AND date<=today (open & te laat samen)
//  overdue:   status=open AND date<today
//  vandaag:   status=open AND date=today
//  later:     status=open AND date>today (incl. opnieuw gepland)
//  afgerond:  status=afgerond
//  gesloten:  status in [gesloten, geannuleerd, uitgesteld]
//  all:       geen filter
const FILTER_TABS = [
  ["open", "Open"],
  ["overdue", "Te laat"],
  ["vandaag", "Vandaag"],
  ["later", "Later gepland"],
  ["afgerond", "Afgerond"],
  ["gesloten", "Gesloten"],
  ["all", "Alles"],
];

const CLOSED_STATUSES = new Set(["gesloten", "geannuleerd", "uitgesteld"]);

function fmtDate(d) { return d || "—"; }

export default function RemindersSection() {
  const { hasPermission, isAdminOrOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState("open");
  const [creating, setCreating] = useState(false);
  const [postponeFor, setPostponeFor] = useState(null);
  const [postponeDate, setPostponeDate] = useState("");
  const [postponeTime, setPostponeTime] = useState("");
  const [form, setForm] = useState({
    title: "", text: "", link_type: "", link_id: "", date: "", time_of_day: "",
    priority: "normaal", repeat: "geen",
  });

  const canCreate = hasPermission("reminders:create");
  const canEdit = hasPermission("reminders:edit");
  const canDelete = isAdminOrOwner();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusF === "open") params.only_open = true;
      else if (statusF === "overdue") params.only_overdue = true;
      else if (statusF === "vandaag") params.only_today = true;
      else if (statusF === "later") params.only_later = true;
      else if (statusF === "gesloten") params.only_closed = true;
      else if (statusF === "afgerond") params.status = "afgerond";
      // statusF === "all" → geen filter
      const r = await adminApi.get("/admin/reminders", { params });
      setItems(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [statusF]);

  useEffect(() => { load(); }, [load]);

  const onCreate = async () => {
    if (!form.title.trim()) { toast.error("Titel is verplicht."); return; }
    try {
      await adminApi.post("/admin/reminders", form);
      toast.success("Reminder aangemaakt");
      setCreating(false);
      setForm({ title: "", text: "", link_type: "", link_id: "", date: "", time_of_day: "", priority: "normaal", repeat: "geen" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onComplete = async (id) => {
    try {
      await adminApi.post(`/admin/reminders/${id}/complete`);
      toast.success("Reminder afgerond");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onPostpone = async () => {
    if (!postponeDate) { toast.error("Kies een nieuwe datum."); return; }
    try {
      const payload = { date: postponeDate };
      if (postponeTime) payload.time_of_day = postponeTime;
      await adminApi.post(`/admin/reminders/${postponeFor.id}/postpone`, payload);
      toast.success("Reminder opnieuw gepland.");
      setPostponeFor(null); setPostponeDate(""); setPostponeTime("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onClose = async (id) => {
    if (!window.confirm("Reminder sluiten? Hij verdwijnt uit beeld maar kan altijd opnieuw geopend worden.")) return;
    try {
      await adminApi.post(`/admin/reminders/${id}/close`);
      toast.success("Reminder gesloten.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onReopen = async (id) => {
    try {
      await adminApi.post(`/admin/reminders/${id}/reopen`);
      toast.success("Reminder opnieuw geopend.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Reminder permanent verwijderen?")) return;
    try {
      await adminApi.delete(`/admin/reminders/${id}`);
      toast.success("Verwijderd");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-section-reminders">
      <AdminPageHeader
        icon={Bell}
        title="Reminders"
        subtitle="Taken en herinneringen voor offertes, klanten en pickup-afspraken"
        actions={
          <>
            <Button onClick={load} variant="outline" size="sm" className="rounded-full border-slate-200 hover:border-brand-500 hover:text-brand-500" data-testid="reminders-refresh">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Vernieuwen
            </Button>
            {canCreate && (
              <Dialog open={creating} onOpenChange={setCreating}>
                <DialogTrigger asChild>
                  <Button data-testid="reminder-new-button" className="rounded-full bg-brand-900 hover:bg-brand-700 text-white font-chivo font-bold uppercase text-xs shadow-brand-soft">
                    <Plus className="w-4 h-4 mr-1.5" /> Nieuwe reminder
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden">
                <DialogHeader>
                  <DialogTitle>Nieuwe reminder</DialogTitle>
                  <DialogDescription>
                    Plan een taak of herinnering en koppel deze optioneel aan een offerte, klant of pickup-afspraak.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-bold">Titel</Label>
                    <Input data-testid="reminder-form-title" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} className="rounded-none mt-1 w-full" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wide font-bold">Tekst (optioneel)</Label>
                    <Textarea rows={3} value={form.text} onChange={(e)=>setForm({...form,text:e.target.value})} className="rounded-none mt-1 w-full" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="min-w-0">
                      <Label className="text-xs uppercase tracking-wide font-bold">Datum</Label>
                      <DatePickerField
                        value={form.date}
                        onChange={(d) => setForm({ ...form, date: d })}
                        testId="reminder-form-date"
                        placeholder="Kies datum"
                        minDate={new Date()}
                      />
                    </div>
                    <div className="min-w-0">
                      <Label className="text-xs uppercase tracking-wide font-bold">Tijd</Label>
                      <Input type="time" data-testid="reminder-form-time" value={form.time_of_day} onChange={(e)=>setForm({...form,time_of_day:e.target.value})} className="rounded-none mt-1 w-full min-w-0 box-border" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="min-w-0">
                      <Label className="text-xs uppercase tracking-wide font-bold">Prioriteit</Label>
                      <Select value={form.priority} onValueChange={(v)=>setForm({...form,priority:v})}>
                        <SelectTrigger className="rounded-none mt-1 w-full" data-testid="reminder-form-priority"><SelectValue /></SelectTrigger>
                        <SelectContent>{PRIORITIES.map((p)=> <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="min-w-0">
                      <Label className="text-xs uppercase tracking-wide font-bold">Herhaling</Label>
                      <Select value={form.repeat} onValueChange={(v)=>setForm({...form,repeat:v})}>
                        <SelectTrigger className="rounded-none mt-1 w-full"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(REPEAT_LABELS).map(([k,l])=> <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="min-w-0">
                      <Label className="text-xs uppercase tracking-wide font-bold">Koppel aan</Label>
                      <Select value={form.link_type} onValueChange={(v)=>setForm({...form,link_type:v})}>
                        <SelectTrigger className="rounded-none mt-1 w-full"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>{Object.entries(LINK_LABELS).map(([k,l])=> <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="min-w-0">
                      <Label className="text-xs uppercase tracking-wide font-bold">Record ID</Label>
                      <Input data-testid="reminder-form-link-id" value={form.link_id} onChange={(e)=>setForm({...form,link_id:e.target.value})} className="rounded-none mt-1 w-full min-w-0 box-border" placeholder="UUID..."/>
                    </div>
                  </div>
                  <Button onClick={onCreate} data-testid="reminder-form-save" className="w-full rounded-full bg-brand-900 hover:bg-brand-700 text-white font-chivo font-bold uppercase text-xs">Aanmaken</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </>
        }
      />

      <div data-testid="reminders-filter-tabs">
        <AdminFilterTabs
          items={FILTER_TABS.map(([k, l]) => ({ key: k, label: l }))}
          value={statusF}
          onChange={setStatusF}
          testidPrefix="reminders-filter"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-brand-soft overflow-x-auto">
        {loading ? <div className="p-6 text-ink-500">Laden...</div> : items.length === 0 ? (
          <div className="p-8 text-center text-ink-500">Geen reminders.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((r) => {
              const prio = PRIORITIES.find((p) => p.value === r.priority) || PRIORITIES[1];
              const isClosed = CLOSED_STATUSES.has(r.status);
              const isOpen = r.status === "open";
              return (
                <li key={r.id} className={`p-4 flex flex-wrap items-start justify-between gap-3 ${r.is_overdue ? "bg-red-50" : ""}`} data-testid={`reminder-row-${r.id}`}>
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {r.is_overdue ? <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-1" /> : <Bell className="w-5 h-5 text-brand-500 flex-shrink-0 mt-1" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-chivo font-bold text-brand-900">{r.title}</span>
                        <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold ${prio.color}`}>{prio.label}</span>
                        <span className="inline-block bg-gray-100 text-gray-700 px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold">{STATUS_LABELS[r.status] || r.status}</span>
                        {r.link_type && (
                          <span className="inline-block bg-blue-100 text-blue-700 px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold">
                            {LINK_LABELS[r.link_type]} {r.link_id?.slice(0,8)}…
                          </span>
                        )}
                        {r.is_overdue && <span className="inline-block bg-red-200 text-red-800 px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold">Te laat</span>}
                      </div>
                      {r.text && <p className="text-xs text-gray-600 mt-1">{r.text}</p>}
                      <div className="text-[11px] text-gray-500 mt-1">
                        {fmtDate(r.date)} {r.time_of_day || ""} · door {r.created_by_email || "—"}
                        {r.repeat && r.repeat !== "geen" && ` · herhaalt ${r.repeat}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {canEdit && isOpen && (
                      <>
                        <button onClick={() => onComplete(r.id)} className="p-1.5 text-gray-400 hover:text-green-600" title="Afronden" data-testid={`reminder-complete-${r.id}`}>
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button onClick={() => { setPostponeFor(r); setPostponeDate(""); setPostponeTime(r.time_of_day || ""); }} className="p-1.5 text-gray-400 hover:text-orange-500" title="Opnieuw plannen" data-testid={`reminder-postpone-${r.id}`}>
                          <Clock className="w-4 h-4" />
                        </button>
                        <button onClick={() => onClose(r.id)} className="p-1.5 text-gray-400 hover:text-gray-700" title="Sluiten (niet afronden)" data-testid={`reminder-close-${r.id}`}>
                          <EyeOff className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {canEdit && isClosed && (
                      <button onClick={() => onReopen(r.id)} className="p-1.5 text-gray-400 hover:text-[#0055FF]" title="Opnieuw openen" data-testid={`reminder-reopen-${r.id}`}>
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    )}
                    {/* Afgerond → alleen verwijderen, geen reopen-knop om
                        per-ongeluk-heropening te voorkomen. */}
                    {canDelete && (
                      <button onClick={() => onDelete(r.id)} className="p-1.5 text-gray-400 hover:text-red-500" title="Verwijderen" data-testid={`reminder-delete-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Dialog open={!!postponeFor} onOpenChange={(o) => { if (!o) { setPostponeFor(null); setPostponeDate(""); setPostponeTime(""); } }}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Reminder opnieuw plannen</DialogTitle>
            <DialogDescription>
              Kies een nieuwe datum en tijd waarop deze reminder opnieuw actief moet worden.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-gray-600 break-words">{postponeFor?.title}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="min-w-0">
              <Label className="text-xs uppercase tracking-wide font-bold">Datum</Label>
              <DatePickerField value={postponeDate} onChange={setPostponeDate} testId="postpone-date-input" placeholder="Nieuwe datum" minDate={new Date()} />
            </div>
            <div className="min-w-0">
              <Label className="text-xs uppercase tracking-wide font-bold">Tijd (optioneel)</Label>
              <Input type="time" data-testid="postpone-time-input" value={postponeTime} onChange={(e) => setPostponeTime(e.target.value)} className="rounded-none mt-1 w-full min-w-0 box-border" />
            </div>
          </div>
          <Button onClick={onPostpone} className="rounded-full bg-brand-900 hover:bg-brand-700 text-white font-chivo font-bold uppercase text-xs w-full" data-testid="postpone-confirm">
            Opnieuw plannen
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
